import React, { useState, useEffect, useMemo } from 'react';
import {
    Box,
    Button,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControl,
    Grid,
    IconButton,
    InputAdornment,
    InputLabel,
    MenuItem,
    Paper,
    Select,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TablePagination,
    TableRow,
    TextField,
    Typography,
    Chip,
    Alert,
    Tooltip
} from '@mui/material';
import { Add, Edit, Delete, Build, Http, Search } from '@mui/icons-material';
import { toast } from 'react-toastify';
import GlobalToolMcpEndpointsDrawer from './GlobalToolsDrawer/GlobalToolMcpEndpointsDrawer';

const API_BASE_URL = `${process.env.REACT_APP_BACKEND_URL}asrosync/admin/global-tools`;

const AGENT_PLATFORMS = [
    { value: 'langchain', label: 'Langchain' },
    { value: 'n8n', label: 'n8n' },
];

const getAuthHeaders = () => {
    const token = localStorage.getItem('token');
    return {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
        ...(token && { 'Authorization': `Bearer ${token}` })
    };
};

const GlobalToolsManager = () => {
    const [tools, setTools] = useState([]);
    const [masterConnectors, setMasterConnectors] = useState([]);
    const [loading, setLoading] = useState(false);
    const [loadingConnectors, setLoadingConnectors] = useState(false);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [editingTool, setEditingTool] = useState(null);
    const [formData, setFormData] = useState({
        toolName: '',
        toolType: '',
        agentPlatform: 'langchain',
        toolUniqueId: '',
        n8nCredentialType: '',
        authConfig: '',
        mcId: '',
        isEnabled: true,
        canCallApis: false
    });
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(25);
    const [searchQuery, setSearchQuery] = useState('');
    const [mcpDrawerOpen, setMcpDrawerOpen] = useState(false);
    const [toolForMcp, setToolForMcp] = useState(null);

    useEffect(() => {
        fetchTools();
        fetchMasterConnectors();
    }, []);

    const fetchTools = async () => {
        setLoading(true);
        try {
            const response = await fetch(API_BASE_URL, {
                method: 'GET',
                headers: getAuthHeaders()
            });

            if (!response.ok) throw new Error('Failed to fetch tools');

            const data = await response.json();
            setTools(data);
        } catch (error) {
            console.error('Error fetching tools:', error);
            toast.error('Failed to load global tools');
        } finally {
            setLoading(false);
        }
    };

    const fetchMasterConnectors = async () => {
        setLoadingConnectors(true);
        try {
            const response = await fetch(`${API_BASE_URL}/master-connectors`, {
                method: 'GET',
                headers: getAuthHeaders()
            });

            if (!response.ok) throw new Error('Failed to fetch master connectors');

            const data = await response.json();
            setMasterConnectors(data);
        } catch (error) {
            console.error('Error fetching master connectors:', error);
            toast.error('Failed to load master connectors');
        } finally {
            setLoadingConnectors(false);
        }
    };

    const handleOpenDialog = (tool = null) => {
        if (tool) {
            setEditingTool(tool);
            setFormData({
                toolName: tool.toolName || '',
                toolType: tool.toolType || '',
                agentPlatform: tool.agentPlatform || 'langchain',
                toolUniqueId: tool.toolUniqueId || '',
                n8nCredentialType: tool.n8nCredentialType || '',
                authConfig: tool.authConfig || '',
                mcId: tool.mcId || '',
                isEnabled: tool.isEnabled !== false,
                canCallApis: tool.canCallApis || false
            });
        } else {
            setEditingTool(null);
            setFormData({
                toolName: '',
                toolType: '',
                agentPlatform: 'langchain',
                toolUniqueId: '',
                n8nCredentialType: '',
                authConfig: '',
                mcId: '',
                isEnabled: true,
                canCallApis: false
            });
        }
        setDialogOpen(true);
    };

    const handleCloseDialog = () => {
        setDialogOpen(false);
        setEditingTool(null);
    };

    const handleInputChange = (field, value) => {
        setFormData(prev => ({
            ...prev,
            [field]: value
        }));
    };

    const handleSave = async () => {
        try {
            // Validate mcId is selected
            if (!formData.mcId) {
                toast.error('Please select a connector for this tool');
                return;
            }

            if (formData.agentPlatform === 'n8n' && !formData.n8nCredentialType?.trim()) {
                toast.error('N8N Credential Type is required for n8n tools');
                return;
            }

            // Validate authConfig JSON
            if (formData.authConfig) {
                try {
                    JSON.parse(formData.authConfig);
                } catch (e) {
                    toast.error('Invalid JSON in Auth Config');
                    return;
                }
            }

            const url = editingTool
                ? `${API_BASE_URL}/${editingTool.toolId}`
                : API_BASE_URL;

            const method = editingTool ? 'PUT' : 'POST';

            const response = await fetch(url, {
                method: method,
                headers: getAuthHeaders(),
                body: JSON.stringify(formData)
            });

            if (!response.ok) {
                const errorText = await response.text();
                throw new Error(errorText || 'Failed to save tool');
            }

            toast.success(editingTool ? 'Tool updated successfully' : 'Tool created successfully');
            handleCloseDialog();
            fetchTools();
        } catch (error) {
            console.error('Error saving tool:', error);
            toast.error('Failed to save tool: ' + error.message);
        }
    };

    const handleDelete = async (toolId) => {
        if (!window.confirm('Are you sure you want to delete this tool?')) return;

        try {
            const response = await fetch(`${API_BASE_URL}/${toolId}`, {
                method: 'DELETE',
                headers: getAuthHeaders()
            });

            if (!response.ok) throw new Error('Failed to delete tool');

            toast.success('Tool deleted successfully');
            fetchTools();
        } catch (error) {
            console.error('Error deleting tool:', error);
            toast.error('Failed to delete tool');
        }
    };

    const handleChangePage = (_, newPage) => setPage(newPage);
    const handleChangeRowsPerPage = (e) => {
        setRowsPerPage(parseInt(e.target.value, 10));
        setPage(0);
    };

    const handleSearchChange = (e) => {
        setSearchQuery(e.target.value);
        setPage(0);
    };

    const filteredTools = useMemo(() => {
        const q = searchQuery.trim().toLowerCase();
        if (!q) return tools;
        return tools.filter((tool) => (tool.toolName || '').toLowerCase().includes(q));
    }, [tools, searchQuery]);

    const paginatedTools = filteredTools.slice(
        page * rowsPerPage,
        page * rowsPerPage + rowsPerPage
    );

    return (
        <Box sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Typography variant="h4" component="h1">
                    <Build sx={{ mr: 1, verticalAlign: 'middle' }} />
                    Global Tools
                </Typography>
                <Button
                    variant="contained"
                    startIcon={<Add />}
                    onClick={() => handleOpenDialog()}
                >
                    Add Tool
                </Button>
            </Box>

            <Alert severity="info" sx={{ mb: 2 }}>
                Global tools are agent-agnostic. Use <strong>MCP Endpoints</strong> on a row to define API
                contracts for that tool. Tenants enable them under Tenant MCP Endpoints.
            </Alert>

            <Box
                sx={{
                    display: 'flex',
                    justifyContent: 'flex-end',
                    alignItems: 'center',
                    mb: 2,
                }}
            >
                <TextField
                    size="small"
                    placeholder="Search by tool name..."
                    value={searchQuery}
                    onChange={handleSearchChange}
                    disabled={loading}
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <Search fontSize="small" />
                            </InputAdornment>
                        ),
                    }}
                    sx={{ minWidth: 280 }}
                />
            </Box>

            {loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                    <CircularProgress />
                </Box>
            ) : (
                <Paper sx={{ display: 'flex', flexDirection: 'column', overflow: 'hidden', height: 'calc(100vh - 280px)', minHeight: 400 }}>
                    <TableContainer sx={{ flex: 1, overflow: 'auto' }}>
                        <Table stickyHeader size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell>Tool Name</TableCell>
                                    <TableCell>Platform</TableCell>
                                    <TableCell>Type</TableCell>
                                    <TableCell>Credential Type</TableCell>
                                    <TableCell>Status</TableCell>
                                    <TableCell align="right">Actions</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {tools.length === 0 && (
                                    <TableRow>
                                        <TableCell colSpan={6} align="center">
                                            <Typography color="textSecondary">No global tools found</Typography>
                                        </TableCell>
                                    </TableRow>
                                )}
                                {tools.length > 0 && filteredTools.length === 0 && (
                                    <TableRow>
                                        <TableCell colSpan={6} align="center">
                                            <Typography color="textSecondary">
                                                No tools match your search
                                            </Typography>
                                        </TableCell>
                                    </TableRow>
                                )}
                                {paginatedTools.map((tool) => (
                                <TableRow key={tool.toolId}>
                                    <TableCell>{tool.toolName}</TableCell>
                                    <TableCell>
                                        <Chip
                                            label={tool.agentPlatform || 'langchain'}
                                            size="small"
                                            variant="outlined"
                                            sx={{ textTransform: 'capitalize' }}
                                        />
                                    </TableCell>
                                    <TableCell>{tool.toolType}</TableCell>
                                    <TableCell>
                                        <code style={{ fontSize: '0.85em' }}>{tool.n8nCredentialType}</code>
                                    </TableCell>
                                    <TableCell>
                                        <Chip
                                            label={tool.isEnabled ? 'Enabled' : 'Disabled'}
                                            sx={{
                                                bgcolor: tool.isEnabled ? '#e8f5e9' : '#f5f5f5',
                                                color: tool.isEnabled ? '#2e7d32' : '#757575',
                                                fontWeight: 500,
                                                fontSize: '0.75rem',
                                                height: '24px'
                                            }}
                                            size="small"
                                        />
                                    </TableCell>
                                    <TableCell align="right">
                                        <Tooltip title="MCP API endpoints for this tool">
                                            <IconButton
                                                size="small"
                                                onClick={() => {
                                                    setToolForMcp(tool);
                                                    setMcpDrawerOpen(true);
                                                }}
                                                color="primary"
                                            >
                                                <Http fontSize="small" />
                                            </IconButton>
                                        </Tooltip>
                                        <IconButton
                                            size="small"
                                            onClick={() => handleOpenDialog(tool)}
                                            color="primary"
                                        >
                                            <Edit fontSize="small" />
                                        </IconButton>
                                        <IconButton
                                            size="small"
                                            onClick={() => handleDelete(tool.toolId)}
                                            color="error"
                                        >
                                            <Delete fontSize="small" />
                                        </IconButton>
                                    </TableCell>
                                </TableRow>
                            ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                    <TablePagination
                        rowsPerPageOptions={[10, 25, 50, 100]}
                        component="div"
                        count={filteredTools.length}
                        rowsPerPage={rowsPerPage}
                        page={page}
                        onPageChange={handleChangePage}
                        onRowsPerPageChange={handleChangeRowsPerPage}
                        sx={{ borderTop: 1, borderColor: 'divider', flexShrink: 0 }}
                    />
                </Paper>
            )}

            {/* Create/Edit Dialog */}
            <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="md" fullWidth>
                <DialogTitle>
                    {editingTool ? 'Edit Tool' : 'Create New Tool'}
                </DialogTitle>
                <DialogContent>
                    <Grid container spacing={2} sx={{ mt: 1 }}>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                label="Tool Name"
                                required
                                value={formData.toolName}
                                onChange={(e) => handleInputChange('toolName', e.target.value)}
                                helperText="e.g., Gmail, Google Calendar"
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <FormControl fullWidth required>
                                <InputLabel>Agent platform</InputLabel>
                                <Select
                                    value={formData.agentPlatform}
                                    onChange={(e) => handleInputChange('agentPlatform', e.target.value)}
                                    label="Agent platform"
                                >
                                    {AGENT_PLATFORMS.map((p) => (
                                        <MenuItem key={p.value} value={p.value}>
                                            {p.label}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                label="Tool Type"
                                value={formData.toolType}
                                onChange={(e) => handleInputChange('toolType', e.target.value)}
                                helperText="e.g., CONNECTOR, Email"
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                label="Tool Unique ID"
                                required
                                value={formData.toolUniqueId}
                                onChange={(e) => handleInputChange('toolUniqueId', e.target.value)}
                                helperText="e.g., gmail_oauth_v1"
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                label="N8N Credential Type"
                                required={formData.agentPlatform === 'n8n'}
                                value={formData.n8nCredentialType}
                                onChange={(e) => handleInputChange('n8nCredentialType', e.target.value)}
                                helperText={
                                    formData.agentPlatform === 'n8n'
                                        ? 'e.g., gmailOAuth2'
                                        : 'Only required for n8n platform'
                                }
                                disabled={formData.agentPlatform !== 'n8n'}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <FormControl fullWidth required>
                                <InputLabel>Link to Connector</InputLabel>
                                <Select
                                    value={formData.mcId}
                                    onChange={(e) => handleInputChange('mcId', e.target.value)}
                                    label="Link to Connector"
                                    disabled={loadingConnectors}
                                    required
                                >
                                    {masterConnectors.map((connector) => (
                                        <MenuItem key={connector.mc_id} value={connector.mc_id}>
                                            {connector.mc_name} ({connector.mc_auth_type || 'OAuth2'})
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs={12}>
                            <TextField
                                fullWidth
                                multiline
                                rows={10}
                                label="Auth Config (JSON)"
                                value={formData.authConfig}
                                onChange={(e) => handleInputChange('authConfig', e.target.value)}
                                helperText="JSON array of auth fields configuration"
                                placeholder='[{"key":"clientId","type":"text","label":"Client ID","required":true}]'
                            />
                        </Grid>
                    </Grid>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseDialog}>Cancel</Button>
                    <Button onClick={handleSave} variant="contained" color="primary">
                        {editingTool ? 'Update' : 'Create'}
                    </Button>
                </DialogActions>
            </Dialog>

            <GlobalToolMcpEndpointsDrawer
                open={mcpDrawerOpen}
                onClose={() => {
                    setMcpDrawerOpen(false);
                    setToolForMcp(null);
                }}
                tool={toolForMcp}
                masterConnectors={masterConnectors}
            />
        </Box>
    );
};

export default GlobalToolsManager;
